import { useEffect, useState } from 'react';
import {
  featuredTalent, findDay, minutesUntil, navActions, nextUp, parseLegacyTarget, shortMeta,
  type NavTarget, type WireframeKey,
} from '@ce/core';
import { btn, dayStyle, divider, kicker, portrait, settle, t } from '../styles';
import { CardArc } from '../components/Chrome';
import { PulseArc, Sheen } from '../components/Ambient';
import { Wireframe } from '../components/Wireframe';
import { CallSheet } from '../components/CallSheet';
import { PromoRotator } from '../components/PromoRotator';
import { propsOf, type WidgetProps } from './context';

/** Which motif sits behind each quick link, when the tile is content-defined. */
const WIRE_FOR: Record<string, WireframeKey> = {
  events: 'special-events',
  talent: 'photo-ops',
  'hall:exhibitors': 'exhibitor-hall',
  'hall:map': 'venue-map',
  schedule: 'schedule',
  quests: 'quests',
};

export function DayPicker({ ctx }: WidgetProps) {
  const { bundle, state, dispatch } = ctx;
  return (
    <div style={{ display: 'flex', gap: 8, padding: '2px 0 16px' }}>
      {bundle.days.map((d) => (
        <button
          key={d.key}
          type="button"
          onClick={() => dispatch({ type: 'setDay', day: d.key })}
          aria-pressed={state.day === d.key}
          style={dayStyle(state.day === d.key)}
        >
          <div style={{ font: '500 9px/1 var(--font-body)', letterSpacing: '0.14em', opacity: 0.65 }}>{d.dow}</div>
          <div style={{ font: '500 17px/1 var(--font-body)', marginTop: 5 }}>{d.num}</div>
        </button>
      ))}
    </div>
  );
}

export function UpNext({ ctx, inst }: WidgetProps) {
  const { bundle, state, dispatch } = ctx;
  const p = propsOf<{ showOpenSchedule: boolean; showFindRoom: boolean }>(inst);
  const next = nextUp(bundle, state.day);

  // Real time-to-start, falling back to the day abbreviation outside event hours.
  const mins = minutesUntil(bundle, next, new Date());
  const nextIn = mins !== null && mins > 0 && mins < 600 ? `${mins} min` : findDay(bundle, next.day).dow;

  return (
    <div
      style={{
        padding: '15px 16px', borderRadius: 'var(--radius-md)',
        background: 'linear-gradient(140deg, var(--color-accent-900), var(--color-surface) 70%)',
        boxShadow: 'var(--shadow-sm)', position: 'relative', overflow: 'hidden',
      }}
    >
      <Sheen delay={1.2} />
      <CardArc size={190} top={-70} right={-70} />
      <PulseArc size={190} top={-70} right={-70} opacity={0.3} delay={2} duration={7} />
      <div style={{ position: 'relative', ...kicker(9.5, 'var(--color-accent-300)') }}>
        {bundle.strings.homeNextKicker} · {nextIn}
      </div>
      <div style={{ font: '500 19px/1.2 var(--font-body)', margin: '9px 0 6px', letterSpacing: '-0.015em', position: 'relative' }}>
        {next.title}
      </div>
      <div style={{ fontSize: 12.5, color: t(0.62), position: 'relative' }}>{shortMeta(next)}</div>
      {(p.showOpenSchedule || p.showFindRoom) && (
        <div style={{ display: 'flex', gap: 8, marginTop: 13, position: 'relative' }}>
          {p.showOpenSchedule && (
            <button type="button" onClick={() => dispatch({ type: 'go', screen: 'schedule' })} style={btn('primary', { fontSize: 12.5 })}>
              Open schedule
            </button>
          )}
          {p.showFindRoom && (
            <button
              type="button"
              onClick={() => navActions({ screen: 'hall', hallTab: 'map' }).forEach(dispatch)}
              style={btn('secondary', { fontSize: 12.5 })}
            >
              Find the room
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function PromoRotatorWidget({ ctx, inst }: WidgetProps) {
  const { bundle, state, dispatch, config } = ctx;
  const p = propsOf<{ label: string }>(inst);
  const shown = p.label ? { ...bundle, strings: { ...bundle.strings, promosLabel: p.label } } : bundle;
  return (
    <PromoRotator
      bundle={shown}
      motion={config.effects.rotatorMotion}
      onNavigate={(target) => navActions(target, bundle, state.day).forEach(dispatch)}
    />
  );
}

interface TileItem { label: string; note: string; target: NavTarget | null; art?: WireframeKey | 'none' }

export function QuickLinks({ ctx, inst }: WidgetProps) {
  const { bundle, state, dispatch } = ctx;
  const p = propsOf<{ useContent: boolean; items: TileItem[] }>(inst);

  // Content-defined tiles keep their live notes ("312 booths"); custom tiles are
  // the admin's own. An empty custom list falls back to content rather than
  // rendering nothing.
  const custom = !p.useContent && p.items.length > 0;
  const tiles: TileItem[] = custom
    ? p.items
    : bundle.quickLinks.map((q) => ({
        label: q.label, note: q.note, target: parseLegacyTarget(q.target), art: WIRE_FOR[q.target] ?? 'none',
      }));

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 9, marginTop: 16 }}>
      {tiles.map((q, i) => (
        <QuickLinkTile
          key={`${q.label}-${i}`}
          label={q.label}
          note={q.note}
          art={q.art && q.art !== 'none' ? q.art : undefined}
          index={i}
          onSelect={() => q.target && navActions(q.target, bundle, state.day).forEach(dispatch)}
        />
      ))}
    </div>
  );
}

function QuickLinkTile({
  label, note, art, index, onSelect,
}: {
  label: string; note: string; art?: WireframeKey; index: number; onSelect: () => void;
}) {
  const [pressed, setPressed] = useState(false);
  return (
    <button
      type="button"
      onClick={onSelect}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      onPointerCancel={() => setPressed(false)}
      style={{
        position: 'relative', overflow: 'hidden', textAlign: 'left',
        padding: '14px 13px 13px', borderRadius: 'var(--radius-md)', minHeight: 92,
        border: `1px solid ${pressed ? 'var(--color-accent)' : divider}`,
        background: 'var(--color-surface)', color: 'var(--color-text)', cursor: 'pointer',
        transform: pressed ? 'scale(0.975)' : 'none',
        transition: 'transform .16s cubic-bezier(.2,.8,.2,1), border-color .18s ease',
        ...settle(index),
      }}
    >
      {art && (
        <div style={{ position: 'absolute', right: -18, bottom: -14, width: 118, height: 74, opacity: pressed ? 0.85 : 0.55, transition: 'opacity .2s ease' }}>
          <Wireframe art={art} strokeWidth={1.1} />
        </div>
      )}
      <div style={{ position: 'relative' }}>
        <div style={{ font: '500 13.5px/1.2 var(--font-body)' }}>{label}</div>
        {note && <div style={{ fontSize: 11, color: t(0.5), marginTop: 4 }}>{note}</div>}
      </div>
    </button>
  );
}

/**
 * Prompt to build the day. Shows until the selected day has a plan, then
 * collapses to a quiet summary — a call to action that never goes away stops
 * reading as a prompt and starts reading as furniture.
 */
export function JourneyCta({ ctx, inst }: WidgetProps) {
  const { bundle, state, dispatch } = ctx;
  const p = propsOf<{ title: string; body: string; action: string }>(inst);
  const planned = (state.journey[state.day] ?? []).length;
  const start = () => dispatch({ type: 'go', screen: 'journey' });

  if (planned > 0) {
    return (
      <button
        type="button"
        onClick={start}
        style={{
          display: 'flex', alignItems: 'center', gap: 11, width: '100%', textAlign: 'left',
          padding: '12px 14px', marginTop: 16, borderRadius: 'var(--radius-md)', cursor: 'pointer',
          border: `1px solid ${divider}`, background: 'transparent', color: 'var(--color-text)',
        }}
      >
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', font: '500 13.5px/1.2 var(--font-body)' }}>
            {planned} session{planned === 1 ? '' : 's'} planned today
          </span>
          <span style={{ display: 'block', fontSize: 11, color: t(0.5), marginTop: 4 }}>Tap to adjust your day</span>
        </span>
        <span style={{ color: 'var(--color-accent)', fontSize: 13 }}>›</span>
      </button>
    );
  }

  return (
    <div
      style={{
        position: 'relative', overflow: 'hidden', marginTop: 16, padding: 16, borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--color-accent-700)', background: 'var(--color-accent-900)',
      }}
    >
      <Sheen delay={0.6} />
      <PulseArc size={150} top={-56} right={-40} opacity={0.26} delay={1.4} />
      <div style={{ position: 'relative' }}>
        <div style={kicker(9.5, 'var(--color-accent-300)')}>{p.title || bundle.strings.journeyCtaTitle}</div>
        <div style={{ fontSize: 12.5, lineHeight: 1.6, color: t(0.7), margin: '9px 0 13px', maxWidth: 280 }}>
          {p.body || bundle.strings.journeyCtaBody}
        </div>
        <button type="button" onClick={start} style={btn('primary', { height: 42, borderRadius: 9, fontSize: 13.5 })}>
          {p.action || bundle.strings.journeyCtaAction}
        </button>
      </div>
    </div>
  );
}

export function CallSheetWidget({ ctx, inst }: WidgetProps) {
  const { bundle, state, dispatch, cmsUrl } = ctx;
  const p = propsOf<{ title: string }>(inst);
  const shown = p.title ? { ...bundle, strings: { ...bundle.strings, callSheetTitle: p.title } } : bundle;
  return (
    <CallSheet
      bundle={shown}
      cmsUrl={cmsUrl}
      day={state.day}
      interests={state.interests}
      saved={state.saved}
      onOpenSchedule={() => dispatch({ type: 'go', screen: 'schedule' })}
      onOpenSession={(id) => dispatch({ type: 'openSession', id })}
      onToggleSave={(id) => dispatch({ type: 'toggleSave', id })}
    />
  );
}

export function FeaturedTalent({ ctx, inst }: WidgetProps) {
  const { bundle, state, dispatch } = ctx;
  const p = propsOf<{ title: string; mode: 'interests' | 'all'; limit: number }>(inst);
  const list = (p.mode === 'all' ? bundle.talent : featuredTalent(bundle, state.interests)).slice(0, p.limit);
  if (!list.length) return null;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '24px 0 11px' }}>
        <div style={kicker()}>{p.title}</div>
        <button type="button" onClick={() => dispatch({ type: 'go', screen: 'talent' })} style={btn('ghost', { fontSize: 12 })}>
          All talent
        </button>
      </div>
      <div className="scroll" style={{ display: 'flex', gap: 11, overflowX: 'auto', margin: '0 -20px', padding: '0 20px 4px' }}>
        {list.map((person) => (
          <button
            key={person.id}
            type="button"
            onClick={() => dispatch({ type: 'openTalent', id: person.id })}
            style={{ flex: 'none', width: 150, textAlign: 'left', padding: 0, border: 0, background: 'transparent', color: 'var(--color-text)', cursor: 'pointer' }}
          >
            <div style={{ height: 178, borderRadius: 'var(--radius-md)', background: 'var(--color-surface)', position: 'relative', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' }}>
              <div style={portrait(person.photoUrl, 22)} />
              <div
                style={{
                  position: 'absolute', inset: 0,
                  background: 'linear-gradient(to top, rgba(var(--color-bg-rgb), 0.92) 0%, rgba(var(--color-bg-rgb), 0.25) 42%, transparent 70%)',
                }}
              />
              <div style={{ position: 'absolute', left: 11, right: 11, bottom: 10 }}>
                <div style={{ font: '500 14px/1.2 var(--font-body)' }}>{person.name}</div>
                {p.mode === 'interests' && (
                  <div style={{ ...kicker(9, 'var(--color-accent-300)', 0.14), marginTop: 6 }}>Your interests</div>
                )}
              </div>
            </div>
            <div style={{ fontSize: 11.5, lineHeight: 1.4, color: t(0.52), marginTop: 9 }}>{person.role}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function SpecialEvents({ ctx, inst }: WidgetProps) {
  const { bundle, state, dispatch } = ctx;
  const p = propsOf<{ title: string; limit: number }>(inst);
  const events = bundle.sessions.filter((s) => s.day === state.day && s.track === 'Special').slice(0, p.limit);
  if (!events.length) return null;

  return (
    <div>
      <div style={{ ...kicker(), margin: '26px 0 11px' }}>{p.title}</div>
      <div style={{ display: 'grid', gap: 9 }}>
        {events.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => dispatch({ type: 'openSession', id: s.id })}
            style={{
              display: 'flex', gap: 13, alignItems: 'center', textAlign: 'left',
              padding: '12px 14px', borderRadius: 'var(--radius-md)', border: `1px solid ${divider}`,
              background: 'transparent', color: 'var(--color-text)', cursor: 'pointer',
            }}
          >
            <div style={{ flex: 'none', width: 42 }}>
              <div style={{ font: '500 13px/1 var(--font-body)' }}>{s.t}</div>
              <div style={{ fontSize: 10, color: t(0.45), marginTop: 3 }}>{s.end}</div>
            </div>
            <div style={{ width: 1, alignSelf: 'stretch', background: divider }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ font: '500 14px/1.25 var(--font-body)' }}>{s.title}</div>
              <div style={{ fontSize: 11.5, color: t(0.5), marginTop: 3 }}>{s.room}</div>
            </div>
            <div style={{ font: '500 10px/1 var(--font-body)', color: 'var(--color-accent-300)', flex: 'none' }}>
              {state.saved.includes(s.id) ? '\u2605' : ''}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/** A live countdown. Ticks each second only when seconds are shown. */
export function Countdown({ inst }: WidgetProps) {
  const p = propsOf<{ label: string; target: string; doneLabel: string; showSeconds: boolean }>(inst);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), p.showSeconds ? 1000 : 30_000);
    return () => window.clearInterval(id);
  }, [p.showSeconds]);

  const diff = Math.max(0, new Date(p.target).getTime() - now);
  const done = diff === 0;
  const d = Math.floor(diff / 86_400_000);
  const h = Math.floor(diff / 3_600_000) % 24;
  const m = Math.floor(diff / 60_000) % 60;
  const s = Math.floor(diff / 1000) % 60;

  const cells: [string, number][] = [['days', d], ['hrs', h], ['min', m]];
  if (p.showSeconds) cells.push(['sec', s]);

  return (
    <div
      style={{
        position: 'relative', overflow: 'hidden', marginTop: 16, padding: '15px 16px',
        borderRadius: 'var(--radius-md)', border: `1px solid ${divider}`, background: 'var(--color-surface)',
      }}
    >
      <Sheen delay={3} strength={0.05} />
      <div style={{ ...kicker(9.5, 'var(--color-accent-300)'), position: 'relative' }}>{done ? p.doneLabel || p.label : p.label}</div>
      {!done && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, position: 'relative' }}>
          {cells.map(([unit, value]) => (
            <div
              key={unit}
              style={{
                flex: 1, padding: '10px 0 8px', textAlign: 'center', borderRadius: 'var(--radius-sm)',
                background: 'rgba(var(--color-accent-rgb), 0.1)',
              }}
            >
              <div style={{ font: '500 22px/1 var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
                {String(value).padStart(2, '0')}
              </div>
              <div style={{ ...kicker(8.5, t(0.45), 0.14), marginTop: 6 }}>{unit}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
